import * as BABYLON from "babylonjs";
import { Vector3 } from "babylonjs";
import maps from "../../../shared/expositionhalls";
import { Booth, BoothImageDisplay, BoothLinkDisplay, BoothVideoDisplay, Carpet, ExpositionHall, ExpositionState, LevelAssets, StaticObject, Orientation, ConfigData, BoothBlogPostDisplay } from "../../../shared/interfaces";
import * as GUI from "babylonjs-gui";
import AssetLoader from "../helper/asset-loader";
import { createBanner, createBoxMesh, createSphereMesh, createTableMesh, getObjectsToInteractAroundMe, loadAsset } from "./helper";
import { AssetIdentifiers, Assets } from "../assets";
import Vec3, { fromObject } from "../../../shared/vec3";
import SynchronizedExpositionState from "../networking/SynchronizedExpositionState";
import ExpositionNetworking from '../networking/ExpositionNetworking';
import KeyStateManager from '../helper/keystate-manager'
import InfoScreen from "../overlay/InfoScreen";
import InteractionPopup from "../overlay/InteractionPopup";
import ImagePlane from "../models/imagePlane";
import Participant from "../models/Participant";
import Link from "../models/link";
import Scene = BABYLON.Scene;
import ShadowGenerator = BABYLON.ShadowGenerator;
import Mesh = BABYLON.Mesh;
import AbstractMesh = BABYLON.AbstractMesh;
import ScreenController from "../overlay/ScreenController";
import VideoPlane from "../models/videoPlane";



class ExpositionRendering {
  zoomFactor: number = 7 // increase to zoom out

  engine: BABYLON.Engine
  camera: BABYLON.ArcRotateCamera

  expositionState: ExpositionState = SynchronizedExpositionState.getInterpolatedState()

  participants: {
    [participantId: string]: Participant
  } = {};
  displayNames: boolean = true;

  currentMap: ExpositionHall
  objectsToInteract: any[] = []

  shadowGenerator: ShadowGenerator

  links: Link[] = []

  initializeRendering(mapId: string) {
    ScreenController.init();

    this.currentMap = maps[mapId].load();
    const scene = this.createInitialScene();

    this.engine.runRenderLoop(() => {
      this.updateState();
      this.updateInfoScreen();
      this.updateScene(scene)
      scene.render()
    });

    window.addEventListener('resize', (event) => {
      this.updateCamera();
    });
  }

  createInitialScene(): BABYLON.Scene {
    const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";

    this.engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });

    const scene = new BABYLON.Scene(this.engine);
    scene.clearColor = BABYLON.Color4.FromHexString('#e2f2ffff');

    // Must be initialized after the scene loads
    GUI.AdvancedDynamicTexture.CreateFullscreenUI("myGUI");

    // setting up rotating camera
    const mapCam = this.currentMap.camera;

    this.camera = new BABYLON.ArcRotateCamera("Camera", 0, 0, 0, new BABYLON.Vector3(mapCam.lookingAt.x, mapCam.lookingAt.y, mapCam.lookingAt.z), scene);
    this.camera.setPosition(new BABYLON.Vector3(mapCam.position.x, mapCam.position.y, mapCam.position.z));
    this.camera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

    this.updateCamera();

    const alpha = -0.75 * Math.PI;
    this.camera.lowerAlphaLimit = alpha;
    this.camera.upperAlphaLimit = alpha;
    this.camera.alpha = alpha;

    const beta = (90 - 35.264) / 180 * Math.PI;
    this.camera.beta = beta;
    this.camera.lowerBetaLimit = beta;
    this.camera.upperBetaLimit = beta;

    this.camera.keysLeft = [];
    this.camera.keysRight = [];
    this.camera.keysUp = [];
    this.camera.keysDown = [];
    this.camera.panningSensibility = 0;
    this.camera.angularSensibilityX = 24000;
    this.camera.attachControl(canvas);

    KeyStateManager.spaceDownEvent = () => {
      this.camera.alpha = alpha;
    }

    ExpositionNetworking.onConfigChange((config: ConfigData) => {
      if(config.displayNamesEnabled !== this.displayNames) {
        this.displayNames = config.displayNamesEnabled;
        const myParticipantId = sessionStorage.getItem('myParticipantId')
        Object.keys(this.participants).forEach(participantId => {
          const itsMe = myParticipantId === participantId;
          const showLabel = itsMe || this.displayNames; // always show own label
          this.participants[participantId].setLabelVisibleEnabled(showLabel);
        })
      }
    })

    this.initializeLights(scene);
    this.loadStaticObjects(scene)
    InteractionPopup.init();

    return scene;
  }

  updateCamera() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const aspect = height / width;
    const relativeZoom = this.zoomFactor / (aspect * 3 / 4);
    this.camera.orthoTop = aspect * relativeZoom;
    this.camera.orthoBottom = -aspect * relativeZoom;
    this.camera.orthoLeft = -relativeZoom;
    this.camera.orthoRight = relativeZoom;
  }

  loadStaticObjects(scene: Scene) {

    const wallMaterial = new BABYLON.StandardMaterial("wallMaterial", scene);
    wallMaterial.diffuseColor = BABYLON.Color3.FromHexString('#ffffff');

    const groundMaterial = new BABYLON.StandardMaterial("groundMaterial", scene);
    groundMaterial.diffuseColor = BABYLON.Color3.FromHexString('#dfdfdf');

    const benchMaterial = new BABYLON.StandardMaterial("benchMaterial", scene);
    benchMaterial.diffuseColor = new BABYLON.Color3(0.7, 0.4, 0.3);

    const linkMaterial = new BABYLON.StandardMaterial("linkMaterial", scene);
    linkMaterial.diffuseColor = new BABYLON.Color3(0.4, 0.4, 1);

    let staticObjects = [];

    const assetLoader = new AssetLoader(scene);
    assetLoader.onFinish(() => {

      const dimension = this.currentMap.dimension;
      const mapObjects: StaticObject[] = this.currentMap.staticObjects;

      for (let i = 0; i < mapObjects.length; i++) {
        const currentObject: StaticObject = mapObjects[i];
        if(currentObject.isVisible) {
          let newObject = null;
          switch (currentObject.assetType) {
            case LevelAssets.WALL_HORIZONTAL:
              newObject = createBoxMesh(currentObject.name, currentObject.position, 1.5, dimension.tileSizeX, dimension.tileSizeZ, wallMaterial, scene, this.shadowGenerator);
              this.addShadows(newObject, this.shadowGenerator);
              break;
            case LevelAssets.WALL_VERTICAL:
              newObject = createBoxMesh(currentObject.name, currentObject.position, 1.5, dimension.tileSizeX, dimension.tileSizeZ, wallMaterial, scene, this.shadowGenerator);
              this.addShadows(newObject, this.shadowGenerator);
              break;
            case LevelAssets.WALL_PILLAR:
              newObject = createBoxMesh(currentObject.name, currentObject.position, 1.5, dimension.tileSizeX, dimension.tileSizeZ, wallMaterial, scene, this.shadowGenerator);
              break;
            case LevelAssets.TABLE:
              newObject = createTableMesh(currentObject.name, currentObject.position, wallMaterial, scene, this.shadowGenerator);
              break;
            case LevelAssets.BANNER:
              newObject = createBanner(currentObject.name, currentObject.position, scene, currentObject.payload, this.shadowGenerator);
              newObject.scaling = new Vector3(dimension.tileSizeX, 1, dimension.tileSizeZ);
              break;
            case LevelAssets.CACTUS1:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_CACTUS, new Vec3(0.015, 0.015, 0.015), currentObject.position);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.CACTUS2:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_CACTUS, new Vec3(0.015, 0.015, 0.015), currentObject.position);
              newObject.rotate(BABYLON.Axis.Y, Math.PI / 2);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.COUCH:
              let couchRotation;
              switch (currentObject.orientation) {
                case Orientation.NORTH:
                  couchRotation = 1;
                  break;
                case Orientation.EAST:
                  couchRotation = 2;
                  break;
                case Orientation.SOUTH:
                  couchRotation = 3;
                  break;
                case Orientation.WEST:
                  couchRotation = 4;
                  break;
              }
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_COUCH, new Vec3(0.06, 0.06, 0.06), currentObject.position, couchRotation);
              console.log(currentObject);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.BOOKSHELF:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_BOOKSHELF,new Vec3(0.025, 0.025, 0.025), currentObject.position);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.CATERING:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_CATERING, new Vec3(-0.015, 0.015, 0.015), currentObject.position.add(new Vec3(1, 0, 0)));
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.POUFS:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_POUFS, new Vec3(0.04, 0.04, 0.04), currentObject.position.add(new Vec3(0, 0, -3)));
              newObject.rotate(BABYLON.Axis.Y, Math.PI / 3);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.PLANTS:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_PLANTS, new Vec3(0.04, 0.04, 0.04), currentObject.position);
              this.shadowGenerator.addShadowCaster(newObject);
              break;
            case LevelAssets.TABLETENNIS:
              newObject = loadAsset(assetLoader, currentObject.name, AssetIdentifiers.MODEL_TABLETENNIS, new Vec3(0.03, 0.03, 0.03), currentObject.position);
              this.shadowGenerator.addShadowCaster(newObject);
              break;

            case LevelAssets.DEFAULT_ASSET:
            default:
              newObject = createSphereMesh(currentObject.name, currentObject.position, 16, 2, null, scene, this.shadowGenerator);
          }

          staticObjects.push(newObject);
        }
      }

      const carpets: Carpet[] = this.currentMap.carpets;
      carpets.forEach(carpet => {
        const plane = BABYLON.MeshBuilder.CreatePlane("carpetplane_" + carpet.textureUrl, { width: carpet.sizeX, height: carpet.sizeZ }, scene);
        plane.rotationQuaternion = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(1, 0, 0), 0.5 * Math.PI);
        plane.receiveShadows = true;

        const imageMaterial = new BABYLON.StandardMaterial("image_" + carpet.textureUrl, scene);
        imageMaterial.diffuseTexture = new BABYLON.Texture(carpet.textureUrl, scene)
        plane.material = imageMaterial;
    
        const planeHolder = new BABYLON.TransformNode('planerHolder_' + carpet.textureUrl);
        plane.parent = planeHolder;
    
        planeHolder.position.x = carpet.position.x
        planeHolder.position.y = 0.1;
        planeHolder.position.z = carpet.position.z
        planeHolder.rotation.y = Math.PI / 2
      });


      // render booths
      this.currentMap.booths.forEach((booth: Booth) => {
        let boothElementCount: number = 0;

        // render image displays:
        booth.images.forEach((imageDisplay: BoothImageDisplay) => {
          new ImagePlane(imageDisplay.thumbnail, imageDisplay.position, imageDisplay.orientation, scene)
          boothElementCount++;
          this.objectsToInteract.push({ type: 'IMAGE', ...imageDisplay });
        })


        // render blogpost displays:
        booth.blogPosts.forEach((blogPostDisplay: BoothBlogPostDisplay) => {
          new ImagePlane(blogPostDisplay.thumbnail, blogPostDisplay.position, blogPostDisplay.orientation, scene)
          boothElementCount++;
          this.objectsToInteract.push({ type: 'POST', ...blogPostDisplay });
        })


        // render link displays:
        booth.links.forEach((linkDisplay: BoothLinkDisplay) => {
          const link = new Link('link' + boothElementCount, linkDisplay.position, assetLoader, scene, this.shadowGenerator)
          this.links.push(link);
          boothElementCount++;
          this.objectsToInteract.push({ type: 'LINK', ...linkDisplay });
        })


        // render video displays:
        booth.videos.forEach((videoDisplay: BoothVideoDisplay) => {
          new VideoPlane(videoDisplay.thumbnail, videoDisplay.position, videoDisplay.orientation, scene, this.shadowGenerator)
          boothElementCount++;
          this.objectsToInteract.push({ type: 'VIDEO', ...videoDisplay });
        })
      })


      assetLoader.disposeAllModels();

      // This is an example how to play a sound:
      //assetLoader.sounds[AssetIdentifiers.SFX_AIRHORN].play();
    });


    assetLoader.loadAssets(Assets);
    //assetsManager.load();

    this.currentMap.grounds.forEach(ground => {
      const groundThickness = 0.25;
      const groundPosition = ground.position.sub(new Vec3(0, groundThickness, 0));
      const groundMesh = createBoxMesh(ground.name, groundPosition, groundThickness, ground.width, ground.height, groundMaterial, scene, undefined);
      groundMesh.receiveShadows = true;
      /*
      const groundMesh = BABYLON.Mesh.CreateGround(ground.name, ground.width, ground.height, 2, scene, false);
      groundMesh.position = ground.position.toBabylonVector3();
      groundMesh.material = groundMaterial;
      groundMesh.receiveShadows = true;
      */
    })
  }

  updateState() {
    this.expositionState = SynchronizedExpositionState.getInterpolatedState();
  }

  updateInfoScreen() {
    const infoItems = Object.values(this.expositionState.participants).map(({ name, ping }) => {
      return {
        name,
        ping
      }
    });
    InfoScreen.update(infoItems);
  }

  updateScene(scene: BABYLON.Scene) {
    const expositionState = this.expositionState;
    const myParticipantId = sessionStorage.getItem('myParticipantId')

    for (let participantId in expositionState.participants) {
      const participantState = expositionState.participants[participantId]

      const playerExists = !!this.participants[participantId];
      if (!playerExists) {
        const itsMe = myParticipantId === participantId;
        const showLabel = itsMe || this.displayNames; // always show own label
        this.participants[participantId] = new Participant(participantId, participantState, scene, this.shadowGenerator, showLabel)
      } else {
        const participant = this.participants[participantId];
        participant.updatePosition(participantState.position, participantState.direction);
      }

      if (participantState.isWalking) {
        // TODO: show nice walking animation here :-)
      }
    }

    
    if (expositionState.participants[myParticipantId]) {
      const playerPosition = fromObject(expositionState.participants[myParticipantId].position);

      // update camera position
      this.camera.target = playerPosition.add(new Vec3(0, 0, 0)).toBabylonVector3();

      const objectsAroundMe = getObjectsToInteractAroundMe(playerPosition, this.objectsToInteract);
      if (objectsAroundMe.length > 0) {
        InteractionPopup.showInteractionPopup(objectsAroundMe[0])
      } else {
        InteractionPopup.hide();
      }
    }

    const activeParticipantIds = Object.keys(expositionState.participants);
    Object.keys(this.participants).forEach(participantId => {
      if (!activeParticipantIds.includes(participantId)) {
        this.participants[participantId].remove()
        delete this.participants[participantId];
      }
    })

    this.links.forEach(link => {
      link.rotate();
    })
  }

  initializeLights(scene: Scene) {
    const mainLight = new BABYLON.HemisphericLight('mainLight', new BABYLON.Vector3(-0.5, 0.8, -0.3), scene);
    mainLight.intensity = 0.9;

    const dirLight = new BABYLON.DirectionalLight("DirectionalLight", new BABYLON.Vector3(1, -0.8, 0.3), scene);

    dirLight.intensity = 0.4  ;

    this.shadowGenerator = new BABYLON.ShadowGenerator(2048, dirLight);
    this.shadowGenerator.usePoissonSampling = true;
    this.shadowGenerator.useBlurExponentialShadowMap = true;
  }

  addShadows(mesh: AbstractMesh | Mesh, shadowGenerator: ShadowGenerator) {
    shadowGenerator.getShadowMap().renderList.push(mesh);
  }

  setZoomFactor(zoomFactor: number) {
    this.zoomFactor = zoomFactor;
    this.updateCamera();
  }
}


export default new ExpositionRendering();